import { type JSX, useState } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import {
  useRenameArtifact,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';

import { renameRejection } from './artifact-editing.js';

/**
 * Rename one file. Gated by the same right as editing its contents ({@link canEditArtifact}), because it is
 * the same kind of change: the file stays where it is and stays as visible as it was.
 *
 * The name the service STORES is what shows afterwards — it sanitizes what it is given and answers with the
 * result — so the dialog closes on that answer rather than on the text the user typed. An empty name is
 * refused here: the service would reject it too, and a round-trip to learn that is a round-trip wasted.
 */
export function ArtifactRenameDialog({
  workspaceId,
  artifact,
  onClose,
}: {
  workspaceId: string;
  artifact: WorkspaceArtifact;
  onClose: () => void;
}): JSX.Element {
  const rename = useRenameArtifact(workspaceId);
  const [name, setName] = useState(artifact.name);
  const [failure, setFailure] = useState<string | null>(null);

  function submit(): void {
    const trimmed = name.trim();
    const rejection = renameRejection(trimmed);
    if (rejection !== null) {
      setFailure(rejection);
      return;
    }
    if (trimmed === artifact.name) {
      onClose();
      return;
    }
    setFailure(null);
    rename.mutate(
      { id: artifact.id, name: trimmed },
      {
        onSuccess: () => onClose(),
        onError: (error) => setFailure(error.message),
      },
    );
  }

  return (
    <AppDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Rename file"
      description={`“${artifact.name}” — the extension is part of the name, so keep it if you want the file to open the same way.`}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        className="mt-2 flex flex-col gap-2"
      >
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-label="New file name"
          autoFocus
        />
        {failure !== null && (
          <p className="text-sm text-destructive">{failure}</p>
        )}
        <div className="mt-3 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={rename.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" variant="brand" disabled={rename.isPending}>
            {rename.isPending ? 'Renaming…' : 'Rename'}
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}
